<template>
  <div class="cart">
    <section class="container">
      <h1 class="title">购物车</h1>
      <div v-if="items.length > 0">
        <div class="table-head">
          <span class="sel-col">
            <label class="select-all">
              <input type="checkbox" v-model="allSelected" />
              <span class="text">全选</span>
            </label>
          </span>
          <span>产品</span>
          <span class="qty-col">数量</span>
          <span class="del-col">删除</span>
          <span class="price-col">价格</span>
        </div>

        <div class="rows">
          <div v-for="it in items" :key="it.id" class="row">
            <div class="sel"><input type="checkbox" v-model="it.selected" /></div>
            <div class="prod">
              <ElImage :src="it.img" fit="cover" class="thumb" />
              <div class="info">
                <div class="name">{{ it.name }}</div>
                <div class="meta">{{ it.skuName }}</div>
              </div>
            </div>
            <div class="qty">
              <ElInputNumber
                v-model="it.prodCount"
                :min="1"
                @change="(val) => updateQty(it, Number(val))"
              />
            </div>
            <div class="del">
              <i class="iconfont icon-shanchu del-btn" @click="remove(it.id)"></i>
            </div>
            <div class="price">￥{{ (it.price * it.prodCount).toLocaleString() }}</div>
          </div>
        </div>

        <div class="summary">
          <div class="total">
            <strong>总计</strong>：￥{{ total.toLocaleString() }} <span class="tax">{{ currencyType }}</span>
          </div>
          <div class="tips">价格会根据您最新下单时间进行调整</div>
          <ElButton class="buy" @click="confirmOrder">立即结算</ElButton>
        </div>
      </div>
      <div v-else class="empty-cart">
        <ElEmpty description="购物车空空如也" />
        <ElButton class="go-shop" @click="router.push('/')">去逛逛</ElButton>
      </div>
    </section>

    <!-- <RecommendSection :items="recommends" /> -->

    <AppFooter />
    <CheckoutModal
      v-model="showCheckout"
      :total="buyTotal"
      :address="address"
      :address-list="addressList"
      :order-id="checkoutOrderId"
      :order-number="checkoutOrderNumber"
      :support-deposit="supportDeposit"
      :create-time="checkoutCreateTime"
      @update:address="setAddress"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useRouter, useRoute } from "#imports";
import AppFooter from "@/components/AppFooter.vue";
import RecommendSection from "@/components/RecommendSection.vue";
import CheckoutModal from "@/components/CheckoutModal.vue";
import { useAuth } from "@/composables/useAuth";

const { currencyType } = useCurrency();
const { checkLogin } = useAuth();

type CartItem = {
  id: number;
  basketId?: number;
  prodId: number;
  skuId: number;
  name: string;
  price: number;
  img: string;
  material: string;
  stone: string;
  size: string;
  skuName: string;
  prodCount: number;
  selected?: boolean;
};
const items = ref<CartItem[]>([]);
const buyTotal = ref<number>(0);
const api = useApi();
const router = useRouter();
const route = useRoute();
const fetchCart = async () => {
  if (!checkLogin()) {
    const redirect = route.fullPath || "/";
    router.push({ path: "/login", query: { redirect } });
    return;
  }
  const r = await api.post<any>(
    "/p/shopCart/info",
    {},
    { server: false, key: "cart-page-info", retry: 0, loading: true }
  );
  const data = (r as any)?.data ?? (r as any)?.raw ?? [];
  const shops = Array.isArray(data) ? data : [];
  const discounts = Array.isArray((shops[0] as any)?.shopCartItemDiscounts)
    ? (shops[0] as any).shopCartItemDiscounts
    : [];
  const rawItems = discounts.flatMap((g: any) =>
    Array.isArray(g?.shopCartItems) ? g.shopCartItems : []
  );
  const mapped: CartItem[] = rawItems.map((it: any) => ({
    id: Number(it?.basketId ?? it?.skuId ?? it?.prodId ?? Date.now()),
    basketId: Number(it?.basketId ?? 0) || undefined,
    prodId: Number(it?.prodId ?? 0),
    skuId: Number(it?.skuId ?? 0),
    name: String(it?.prodName ?? it?.skuName ?? ""),
    price: Number(it?.price ?? it?.skuPrice ?? it?.actualPrice ?? 0),
    img: String(it?.pic ?? it?.imgUrl ?? ""),
    material: "",
    stone: "",
    size: String(it?.skuName ?? ""),
    skuName: String(it?.skuName ?? ""),
    prodCount: Number(it?.prodCount ?? 1),
    selected: true,
  }));
  items.value = mapped;
  try {
    window.dispatchEvent(new CustomEvent("cart-changed"));
  } catch {}
};
onMounted(fetchCart);

const updateQty = async (item: CartItem, newQty: number) => {
  const oldQty = item.prodCount;
  if (newQty < 1) return;

  const res = await api.post<any>("/p/shopCart/changeItem", {
    count: newQty,
    prodId: item.prodId,
    skuId: item.skuId,
    shopId: 1,
  });

  if (res.ok) {
    // API might return success but we already updated v-model locally or we need to confirm
    // If the API returns the new state, we could use it, but usually we just trust the input if success
    try {
      window.dispatchEvent(new CustomEvent("cart-changed"));
    } catch {}
  } else {
    ElMessage.error(res.msg || "修改数量失败");
    // Revert
    item.prodCount = oldQty;
  }
};

const remove = async (id: number) => {
  const item = items.value.find((i) => i.id === id);
  if (!item) return;

  try {
    await ElMessageBox.confirm("确定要删除该商品吗？", "提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });

    const basketId = item.basketId ?? item.id;
    if (basketId) {
      const res = await api.del<any>("/p/shopCart/deleteItem", [basketId]);
      if (res.ok) {
        ElMessage.success("删除成功");
        items.value = items.value.filter((i) => i.id !== id);
        try {
          window.dispatchEvent(new CustomEvent("cart-changed"));
        } catch {}
      } else {
        ElMessage.error(res.msg || "删除失败");
      }
    } else {
      items.value = items.value.filter((i) => i.id !== id);
      try {
        window.dispatchEvent(new CustomEvent("cart-changed"));
      } catch {}
    }
  } catch {}
};
const total = computed(() =>
  items.value.filter((i) => i.selected).reduce((s, i) => s + i.price * i.prodCount, 0)
);
const allSelected = computed({
  get() {
    return items.value.length > 0 && items.value.every((i) => i.selected);
  },
  set(v: boolean) {
    items.value.forEach((i) => (i.selected = v));
  },
});

const showCheckout = ref(false);
const address = ref({});
const addressList = ref<any[]>([]);
const checkoutOrderId = ref<number | undefined>(undefined);
const checkoutOrderNumber = ref<string | undefined>(undefined);
const supportDeposit = ref<number | undefined>(undefined);
const checkoutCreateTime = ref<string | undefined>(undefined);

const setAddress = (v: { full: string; name: string; phone: string }) => {
  address.value = v;
};

const getAddrId = async () => {
  const r = await api.get<any>("/p/address/list", {}, { server: false, key: "address-list", retry: 0 });
  const list = (r as any)?.data ?? (r as any)?.raw ?? [];
  const arr = Array.isArray(list) ? list : [];
  addressList.value = arr;
  const isDefault = (a: any) => a?.commonAddr == 1;
  const chosen = arr.find(isDefault) ?? arr[0];
  if (chosen) {
    const full = [chosen?.province, chosen?.city, chosen?.area, chosen?.addrDetail, chosen?.address, chosen?.detail].filter(Boolean).join("");
    const name = String(chosen?.receiver ?? "");
    const phone = String(chosen?.mobile ?? chosen?.tel ?? chosen?.phone ?? "");
    if (full || name || phone) address.value = { full, name, phone };
  }
  const addrId = chosen ? Number(chosen?.id ?? chosen?.addrId ?? chosen?.addressId) : undefined;
  return Number.isFinite(addrId as number) ? (addrId as number) : undefined;
};

const confirmOrder = async () => {
  if (!checkLogin()) {
    const redirect = route.fullPath || "/";
    router.push({ path: "/login", query: { redirect } });
    return;
  }
  const basketIds = items.value
    .filter((i) => i.selected)
    .map((i) => Number(i.basketId ?? i.id))
    .filter((n) => Number.isFinite(n));
  if (!basketIds.length) {
    ElMessage.warning("请先选择要结算的商品");
    return;
  }

  const addrId = await getAddrId();
  const body: any = { basketIds };
  if (addrId !== undefined) body.addrId = addrId;

  const resp = await api.post<any>("/p/order/confirm", body, { server: false, key: `order-confirm-${Date.now()}`, retry: 0 });
  if (resp.code == '00000') {
    const data = (resp as any)?.data ?? (resp as any)?.raw ?? {};
    buyTotal.value = Number((data as any)?.actualTotal ?? 0);
    const hasAddr = !!(data && (data as any).userAddr && Object.keys((data as any).userAddr).length > 0);
    if (hasAddr) {
      const submitBody = { orderShopParam: [{ shopId: 1 }],supportDeposit: 0 };
      const submitResp = await api.post<any>("/p/order/submit", submitBody, { server: false, key: `order-submit-${Date.now()}`, retry: 0 });
      if (submitResp.code == '00000') {
        const sData = (submitResp as any)?.data ?? (submitResp as any)?.raw ?? {};
        checkoutOrderId.value = sData?.orderId;
        // 尝试获取 orderNumber 或 orderNumbers
        checkoutOrderNumber.value = sData?.orderNumber ?? sData?.orderNumbers?.split(",")?.[0];
        checkoutCreateTime.value = sData?.createTime;
        showCheckout.value = true;
      } else {
        ElMessage.error(submitResp.msg || "提交订单失败");
      }
    } else {
      checkoutOrderId.value = undefined;
      checkoutOrderNumber.value = undefined;
      showCheckout.value = true;
    }
  } else {
    ElMessage.error(resp.msg || "创建订单失败");
  }
};

watch(showCheckout, (val) => {
  if (!val) {
    fetchCart();
  }
});

const recommends = [
  {
    id: 11,
    name: "月影耳钉",
    price: 299,
    img: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc0?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: 12,
    name: "星轨手链",
    price: 499,
    img: "https://images.unsplash.com/photo-1515562141207-8d5a4d3b24d6?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: 13,
    name: "晨雾项链",
    price: 799,
    img: "https://images.unsplash.com/photo-1603575449155-9a4b4a5d1b7e?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: 14,
    name: "清雅戒指",
    price: 399,
    img: "https://images.unsplash.com/photo-1617033650354-3c2ea3c3f55b?q=80&w=800&auto=format&fit=crop",
  },
];
</script>

<style scoped>
.cart {
  font-size: 0.16rem;
}
.container {
  width: 100%;
  padding: 0 0.9rem;
}
.title {
  font-size: 0.4rem;
  margin: 0.6rem 0;
  font-weight: 400;
  color: #121212;
}
.table-head {
  display: grid;
  grid-template-columns: 0.18fr 1.6fr 0.9fr 0.4fr 0.6fr;
  font-size: 0.14rem;
  color: #424446;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.16rem;
}
.sel,
.sel-col {
  display: flex;
  align-items: center;
  justify-content: center;
}
.select-all {
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
}
.sel input[type="checkbox"],
.sel-col input[type="checkbox"],
.select-all input[type="checkbox"] {
  accent-color: #000;
}
.qty-col {
  text-align: center;
}
.del-col {
  text-align: center;
}
.price-col {
  text-align: right;
}
.rows {
}
.row {
  display: grid;
  grid-template-columns: 0.18fr 1.6fr 0.9fr 0.4fr 0.6fr;
  align-items: center;
  padding: 0.24rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.prod {
  display: grid;
  grid-template-columns: 0.9rem 1fr;
  gap: 0.2rem;
  align-items: center;
}
.thumb {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 0.06rem;
}
.info {
}
.name {
  font-size: 0.16rem;
  margin-bottom: 0.06rem;
  color: #424446;
}
.meta {
  color: #939597;
  font-size: 0.14rem;
}
.qty {
  display: flex;
  gap: 0.16rem;
  justify-content: center;
  align-items: center;
}
.del {
  display: flex;
  gap: 0.16rem;
  justify-content: center;
  align-items: center;
}
.del-btn {
  cursor: pointer;
  font-size: 0.24rem;
  color: #333537;
}
.del-btn:hover {
  color: #121212;
}
.price {
  text-align: right;
  color: #121212;
  font-size: 0.18rem;
}
.summary {
  display: grid;
  grid-template-columns: 1fr;
  justify-items: end;
  margin: 0.3rem 0 1.88rem;
}
.total {
  font-size: 0.18rem;
  color: #121212;
}
.tips {
  font-size: 0.14rem;
  color: #939597;
  margin-top: 0.2rem;
}
.buy {
  width: 3.6rem;
  height: 0.54rem;
  background: #121212;
  border-color: #121212;
  color: #fff;
  margin-top: 0.36rem;
}

@media (max-width: 960px) {
  .container {
    padding: 0 0.24rem;
  }
  .row,
  .table-head {
    grid-template-columns: 0.18fr 1.6fr 0.9fr 0.4fr 0.6fr;
  }
}
@media (max-width: 640px) {
  .row {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }
  .qty,
  .price {
    justify-content: start;
    text-align: left;
  }
}

.empty-cart {
  padding: 0.8rem 0 1.2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.go-shop {
  width: 2rem;
  height: 0.64rem;
  background: #121212;
  border-color: #121212;
  color: #fff;
  margin-top: 0.2rem;
}
.tax {
  color: #666;
  font-size: 0.14rem;
}
</style>
